import { useEffect, useState } from 'react';
import { Check, Download, Laptop, RefreshCw, WifiOff } from 'lucide-react';
import { getDeferredInstallPrompt, clearDeferredInstallPrompt, type BeforeInstallPromptEvent, type PwaUpdate } from '../../services/pwa';

export const PwaNotices = ({ offline, update }: { offline: boolean; update: PwaUpdate | null }) => {
  if (!offline && !update) return null;
  return (
    <div className="pwa-notices" aria-live="polite">
      {offline && <section className="pwa-notice"><WifiOff size={19} aria-hidden="true" /><div><strong>You are offline</strong><span>Your cached planner and local data remain available.</span></div></section>}
      {update && <section className="pwa-notice update-notice"><RefreshCw size={19} aria-hidden="true" /><div><strong>Update {update.version} is ready</strong><span>Reload when you are ready. Your local planner data will remain in this browser.</span></div><span className="pwa-notice-actions"><button type="button" className="text-button" onClick={update.dismiss}>Later</button><button type="button" className="primary-button" onClick={update.activate}>Update now</button></span></section>}
    </div>
  );
};

export const InstallGuide = () => {
  const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 768;
  const isStandalone = () => typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true
  );

  const [desktop, setDesktop] = useState(isDesktop);
  const [installed, setInstalled] = useState(isStandalone);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(() => getDeferredInstallPrompt());
  const [showHelper, setShowHelper] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const updateStatus = () => {
      setDesktop(window.innerWidth >= 768);
      setInstalled(media.matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);
    };
    
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    media.addEventListener('change', updateStatus);
    window.addEventListener('resize', updateStatus);
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      updateStatus();
    });
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      media.removeEventListener('change', updateStatus);
      window.removeEventListener('resize', updateStatus);
      window.removeEventListener('appinstalled', updateStatus);
    };
  }, []);

  if (!desktop) return null;

  const handleInstallClick = async () => {
    const promptToUse = installPrompt ?? getDeferredInstallPrompt();
    if (promptToUse) {
      await promptToUse.prompt();
      const choice = await promptToUse.userChoice;
      if (choice.outcome === 'accepted') {
        clearDeferredInstallPrompt();
        setInstallPrompt(null);
        setInstalled(true);
      }
    } else {
      setShowHelper(true);
    }
  };

  if (installed) {
    return (
      <section className="settings-card install-guide installed-guide" aria-labelledby="install-guide-title">
        <div className="install-guide-heading">
          <span className="more-card-icon" aria-hidden="true"><Laptop size={21} /></span>
          <div>
            <p className="eyebrow">Desktop app</p>
            <h2 id="install-guide-title">Installed on this computer</h2>
          </div>
        </div>
        <p>Spooky Plan is running in standalone desktop mode with full offline access.</p>
        <div className="installed-badge" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          marginTop: '2px',
          borderRadius: '11px',
          border: '1px solid var(--color-border)',
          background: 'color-mix(in srgb, var(--color-primary-soft) 40%, var(--color-surface))',
          color: 'var(--color-primary-hover)',
          fontWeight: 750,
          fontSize: '12px'
        }}>
          <Check size={16} aria-hidden="true" />
          <span>App is installed & ready</span>
        </div>
      </section>
    );
  }

  return (
    <section className="settings-card install-guide" aria-labelledby="install-guide-title">
      <div className="install-guide-heading">
        <span className="more-card-icon" aria-hidden="true"><Download size={21} /></span>
        <div>
          <p className="eyebrow">Desktop browser</p>
          <h2 id="install-guide-title">Install Spooky Plan on this computer</h2>
        </div>
      </div>
      <p>Install this app on your desktop for quick offline access and a dedicated window.</p>
      <div className="install-actions">
        <button
          type="button"
          className="primary-button install-button"
          onClick={handleInstallClick}
        >
          <Download size={17} aria-hidden="true" />
          Install on computer
        </button>
      </div>
      <p className="install-helper-text">
        {showHelper
          ? "👉 Click the install icon in your browser's address bar (top right) or go to menu > Apps > Install. Mobile instructions are in the delivery PDF."
          : "Depending on your browser, you can also use your browser's install option when available. Mobile installation instructions are provided separately in the delivery PDF."}
      </p>
    </section>
  );
};



